import { X, ExternalLink, TrendingUp, Users, Calendar, DollarSign, Shield } from 'lucide-react';

interface CollaborationDrawerProps {
  collaboration: any;
  isOpen: boolean;
  onClose: () => void;
}

export default function CollaborationDrawer({ collaboration, isOpen, onClose }: CollaborationDrawerProps) {
  if (!isOpen || !collaboration) return null;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getConfidenceConfig = (score: string) => {
    const config = {
      high: {
        label: 'High',
        description: 'Collaboration confirmée avec forte probabilité',
        class: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
        icon: 'text-emerald-400',
      },
      medium: {
        label: 'Medium',
        description: 'Collaboration probable nécessitant validation',
        class: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        icon: 'text-amber-400',
      },
      low: {
        label: 'Low',
        description: 'Collaboration possible mais incertaine',
        class: 'bg-white/[0.04] text-white/50 border-white/[0.08]',
        icon: 'text-white/40',
      },
    };
    return config[score as keyof typeof config] || config.medium;
  };

  const confidenceConfig = getConfidenceConfig(collaboration.confidence_score);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-[#141414] border-l border-white/[0.08] shadow-2xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-[#141414] border-b border-white/[0.08] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-white">Détails de la collaboration</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white/50" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {collaboration.post_image_url && (
            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden border border-white/[0.08]">
              <img
                src={collaboration.post_image_url}
                alt="Post"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Image+indisponible';
                }}
              />
            </div>
          )}

          <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">
                  {collaboration.brand_name.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-white/40 font-semibold uppercase tracking-widest mb-1">Marque détectée</div>
                <div className="text-xl font-bold text-white">{collaboration.brand_name}</div>
                <div className="flex items-center gap-3 mt-2 text-sm text-white/50">
                  <span className="font-medium">{collaboration.platform}</span>
                  <span>•</span>
                  <span>{collaboration.content_type}</span>
                </div>
              </div>
            </div>
          </div>

          {collaboration.caption && (
            <div>
              <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Caption du post</h3>
              <p className="text-sm text-white/60 leading-relaxed bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                {collaboration.caption}
              </p>
            </div>
          )}

          <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
            <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-4">Informations créateur</h3>
            <div className="flex items-start gap-4 mb-5">
              <img
                src={collaboration.creator_avatar || 'https://via.placeholder.com/64'}
                alt={collaboration.creator_name}
                className="w-16 h-16 rounded-full object-cover border-2 border-white/[0.08]"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/64';
                }}
              />
              <div className="flex-1">
                <div className="text-lg font-bold text-white">
                  {collaboration.creator_name}
                </div>
                <div className="text-sm text-white/40">@{collaboration.creator_handle}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/[0.04] rounded-lg p-3 border border-white/[0.06]">
                <div className="flex items-center gap-2 text-white/40 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs font-medium">Followers</span>
                </div>
                <div className="text-xl font-bold text-white">
                  {formatNumber(collaboration.followers)}
                </div>
              </div>
              <div className="bg-white/[0.04] rounded-lg p-3 border border-white/[0.06]">
                <div className="flex items-center gap-2 text-white/40 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Engagement</span>
                </div>
                <div className="text-xl font-bold text-emerald-400">
                  {collaboration.engagement_rate.toFixed(2)}%
                </div>
              </div>
              {collaboration.category && (
                <div className="bg-white/[0.04] rounded-lg p-3 border border-white/[0.06] col-span-2">
                  <div className="text-xs font-medium text-white/40 mb-1">Catégorie</div>
                  <div className="text-sm font-semibold text-white">
                    {collaboration.category}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-emerald-500/[0.08] border border-emerald-500/20 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <h3 className="text-sm font-semibold text-white/80">Estimation du prix</h3>
            </div>
            <div className="text-3xl font-bold text-emerald-400">
              {collaboration.estimated_price_min}€ — {collaboration.estimated_price_max}€
            </div>
            <p className="text-xs text-white/40 mt-2">
              Estimation basée sur le nombre de followers, le taux d'engagement et la plateforme
            </p>
          </div>

          <div className={`border rounded-xl p-5 ${confidenceConfig.class}`}>
            <div className="flex items-start gap-3">
              <Shield className={`w-5 h-5 ${confidenceConfig.icon} flex-shrink-0 mt-0.5`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold">Score de confiance</h3>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${confidenceConfig.class}`}>
                    {confidenceConfig.label}
                  </span>
                </div>
                <p className="text-sm">{confidenceConfig.description}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
            <div className="flex items-center gap-2 text-white/40 mb-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Date de détection</span>
            </div>
            <div className="text-sm text-white/80">
              {formatDate(collaboration.date_detected)}
            </div>
          </div>

          {collaboration.post_url && (
            <a
              href={collaboration.post_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-white text-black rounded-xl hover:bg-white/90 transition-all font-semibold"
            >
              <ExternalLink className="w-5 h-5" />
              Voir le post original
            </a>
          )}
        </div>
      </div>
    </>
  );
}
